// The SPA's one door to the API (tanstack-router-structure-guide.md → Stack): same origin,
// session cookie, shapes from packages/contracts. Never a reseller API key, never /v3.
import { ErrorBody, type ErrorCode, type ErrorDetail } from "@kilat/contracts/errors";
import type { z } from "zod";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details: ErrorDetail[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type FetchImpl = (url: string, init: RequestInit) => Promise<Response>;

type RequestOptions<T> = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
};

export function createApiClient(fetchImpl: FetchImpl) {
  async function request<T = unknown>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    if (!path.startsWith("/api/")) throw new Error(`the portal calls /api/* only, not ${path}`);
    const { method = "GET", body, schema, signal } = options;
    const headers = new Headers({ accept: "application/json" });
    if (body !== undefined) headers.set("content-type", "application/json");

    const res = await fetchImpl(path, {
      method,
      headers,
      credentials: "same-origin",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

    if (!res.ok) {
      const parsed = ErrorBody.safeParse(await res.json().catch(() => null));
      if (!parsed.success) throw new ApiError(res.status, "internal", `HTTP ${res.status}`);
      const { code, message, details } = parsed.data.error;
      throw new ApiError(res.status, code, message, details);
    }
    if (res.status === 204) return undefined as T;
    const data: unknown = await res.json();
    return schema ? schema.parse(data) : (data as T);
  }

  return { request };
}

export const api = createApiClient((url, init) => fetch(url, init));
